import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { ButtonItem } from './interface';

interface BlockOption {
  block_id: number;
  label: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (button: ButtonItem) => void;
  allBlocks: BlockOption[];
  editing?: ButtonItem | null;
}

export function LocalButtonModal({
  open,
  onClose,
  onSave,
  allBlocks,
  editing,
}: Props) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState<
    'none' | 'url' | 'web_app' | 'callback' | 'programmed'
  >('none');
  const [keyboardType, setKeyboardType] = useState<'inline' | 'reply'>(
    'inline',
  );
  const [url, setUrl] = useState('');
  const [callbackBlock, setCallbackBlock] = useState<number | ''>('');
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [orderX, setOrderX] = useState(1);
  const [orderY, setOrderY] = useState(1);

  useEffect(() => {
    if (!editing) return;
    setLabel(editing.label || '');
    setType(editing.type || 'none');
    setKeyboardType(editing.keyboardType);
    setUrl(editing.url || '');
    setCallbackBlock(editing.nextBlockId || '');
    setIsFullWidth(editing.isFullWidth || false);
    setOrderX(editing.orderX || 1);
    setOrderY(editing.orderY || 1);
  }, [editing]);

  const TYPE_OPTIONS = [
    { value: 'none', label: 'Без дії', description: 'Кнопка без функціоналу' },
    { value: 'url', label: 'Посилання', description: 'Відкрити посилання' },
    { value: 'web_app', label: 'Web App', description: 'Запустити Web App' },
    { value: 'callback', label: 'Callback', description: 'Перехід на блок' },
    { value: 'programmed', label: 'Програмна', description: 'Системна кнопка' },
  ];

  const handleSave = () => {
    onSave({
      id: editing?.id || Date.now(),
      label,
      type,
      keyboardType,
      url: type === 'url' || type === 'web_app' ? url : null,
      nextBlockId: type === 'callback' ? Number(callbackBlock) : null,
      isFullWidth,
      orderX: isFullWidth ? 0 : orderX,
      orderY: isFullWidth ? 0 : orderY,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {editing ? 'Редагувати кнопку' : 'Додати кнопку'}
        <IconButton edge="end" color="inherit" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Назва"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
          />

          <Box>
            <Typography variant="subtitle1" mb={1}>
              Тип кнопки
            </Typography>

            <Stack direction="row" gap={1.5} flexWrap="wrap">
              {TYPE_OPTIONS.map((opt) => {
                const disabled =
                  type === 'programmed' || opt.value === 'programmed';
                return (
                  <Box
                    key={opt.value}
                    onClick={() => !disabled && setType(opt.value as any)}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      border:
                        type === opt.value
                          ? '2px solid #4f46e5'
                          : '1px solid #ccc',
                      opacity: disabled ? 0.5 : 1,
                      width: 150,
                    }}
                  >
                    <Typography fontWeight="bold">{opt.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {opt.description}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          {(type === 'url' || type === 'web_app') && (
            <TextField
              label="URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              fullWidth
            />
          )}

          {type === 'callback' && (
            <FormControl fullWidth>
              <InputLabel>Оберіть блок</InputLabel>
              <Select
                value={callbackBlock}
                onChange={(e) => setCallbackBlock(Number(e.target.value))}
              >
                {allBlocks.map((b) => (
                  <MenuItem key={b.block_id} value={b.block_id}>
                    {b.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={isFullWidth}
                onChange={(e) => setIsFullWidth(e.target.checked)}
              />
            }
            label="Повна ширина"
          />

          {!isFullWidth && (
            <>
              <TextField
                type="number"
                label="Номер рядку"
                value={orderY}
                onChange={(e) => setOrderY(Number(e.target.value))}
                fullWidth
              />
              <TextField
                type="number"
                label="Порядок у ряду"
                value={orderX}
                onChange={(e) => setOrderX(Number(e.target.value))}
                fullWidth
              />
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button variant="contained" onClick={handleSave}>
          Зберегти
        </Button>
      </DialogActions>
    </Dialog>
  );
}
